import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import SearchForm from "../../../components/form/SearchForm.jsx";
import TableFooter from "../../../components/TableFooter.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { isLoadingEstablishments, selectEstablishments } from "../selectors";
import { fetchEstablishments } from "../thunks.js";

const messages = {
  filter: {
    id: "modules.EmailEstablishment.components.Establishments.filter",
    description: "Filter label",
    defaultMessage: "Filter on name",
  },
  email: {
    id: "modules.EmailEstablishment.components.Establishments.email",
    description: "Email label",
    defaultMessage: "Email address",
  },
  noEmail: {
    id: "modules.EmailEstablishment.components.Establishments.noEmail",
    description: "No email set message",
    defaultMessage: "Email address not set",
  },
};

const Establishments = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState();
  const pageSize = 10;
  const [searchTitle, setSearchTitle] = useState("");

  const establishments = useSelector(selectEstablishments);
  const isLoading = useSelector(isLoadingEstablishments);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchEstablishments({ page, pageSize, searchTitle }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize, searchTitle]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    setSearchTitle(e.target.value);
  };

  const onSelectPage = (newPage) => {
    setPage(newPage);
  };

  const dismissError = () => setError(null);

  const mapEstablishmentToTableRow = (establishment) => {
    if (!establishment.principalId || !establishment.name) return null;
    return (
      <tr key={`establishment-${establishment.principalId}`}>
        <td>
          <Link
            to={`/email-establishment/establishment/edit/${establishment.principalId}`}
          >
            {establishment.principalId}
          </Link>
        </td>
        <td>{establishment.name}</td>
        <td>{establishment.emailAddress || formatMessage(messages.noEmail)}</td>
      </tr>
    );
  };

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {formatMessage(genericTranslations.error)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      <SearchForm
        searchTitle={searchTitle}
        onSearchChange={handleSearchChange}
      />

      {isLoading && (
        <span className="align-middle">
          {formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading && establishments.length === 0 && (
        <span className="align-middle">
          {formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {!isLoading && establishments.length > 0 && (
        <Table responsive>
          <thead>
            <tr>
              <td>Link</td>
              <td>{formatMessage(genericTranslations.name)}</td>
              <td>{formatMessage(messages.email)}</td>
            </tr>
          </thead>
          <tbody>{establishments.map(mapEstablishmentToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter onSelectPage={onSelectPage} page={page} pages={pages} />
    </div>
  );
};

export default Establishments;
